import { useState, type ReactNode } from "react";
import { Loader2, Pause, Play } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useDisconnectChatChannel, useSetChatChannelPaused } from "@/hooks/agent/useChatChannels";
import { ChatChannelInstall } from "@/types/agent/chat-channels";

export const PRIMARY_BUTTON =
  "min-h-[44px] bg-gradient-to-r from-genie-primary to-genie-secondary hover:from-genie-primary-hover hover:to-genie-secondary text-white font-semibold px-6 shadow-lg shadow-genie-primary/30 hover:shadow-xl hover:shadow-genie-primary/40 hover:scale-105 transition-all duration-200 disabled:opacity-50 disabled:hover:scale-100 disabled:hover:shadow-lg";

export function SectionHeading({ children }: { children: ReactNode }) {
  return (
    <h4 className="flex items-center gap-2 text-sm font-semibold text-gray-700">
      <span className="w-1 h-4 rounded-full bg-gradient-to-b from-genie-primary to-genie-secondary" aria-hidden="true" />
      {children}
    </h4>
  );
}

export function ChatCode({ children }: { children: ReactNode }) {
  return <code className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-800 text-[13px] break-words">{children}</code>;
}

export function CheatSheetCard({ title, children, className = "" }: { title: string; children: ReactNode; className?: string }) {
  return (
    <div
      className={`relative min-w-0 overflow-hidden p-4 pt-5 bg-gray-50 rounded-xl border border-gray-200 text-sm text-gray-600 leading-relaxed ${className}`}
    >
      <span
        className="absolute inset-x-0 top-0 h-[3px] bg-gradient-to-r from-genie-primary to-genie-secondary"
        aria-hidden="true"
      />
      <p className="font-semibold text-gray-900">{title}</p>
      <p className="mt-1">{children}</p>
    </div>
  );
}

interface WorkspaceRowProps {
  agentId: string;
  install: ChatChannelInstall;
  /** "Slack" or "Teams", for the row's wording. */
  platformLabel: string;
  fallbackName: string;
  removeHint?: string;
}

export function WorkspaceRow({ agentId, install, platformLabel, fallbackName, removeHint }: WorkspaceRowProps) {
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);
  const setPausedMutation = useSetChatChannelPaused(agentId);
  const disconnectMutation = useDisconnectChatChannel(agentId);
  const isPaused = Boolean(install.paused_at);
  const name = install.workspace_name || fallbackName;

  const handleTogglePaused = () => {
    setPausedMutation.mutate(
      { installId: install.id, paused: !isPaused },
      {
        onSuccess: () => toast.success(isPaused ? `Answering in ${name} again` : `Paused in ${name}`),
        onError: () => toast.error("Couldn't update the workspace"),
      },
    );
  };

  const handleDisconnect = () => {
    disconnectMutation.mutate(install.id, {
      onSuccess: () => toast.success(removeHint ? `Removed from ${name}. ${removeHint}` : `Removed from ${name}`),
      onError: () => toast.error("Couldn't disconnect the workspace"),
    });
  };

  return (
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 bg-gray-50 rounded-xl border border-gray-200">
      <div className="min-w-0">
        <p className="flex items-center gap-2 text-sm font-semibold text-gray-900">
          <span
            className={`w-2 h-2 shrink-0 rounded-full ${isPaused ? "bg-gray-300" : "bg-gradient-to-r from-genie-primary to-genie-secondary"}`}
            aria-hidden="true"
          />
          <span className="truncate">{name}</span>
        </p>
        <p className="text-xs text-gray-500 mt-0.5">
          {isPaused ? "Paused — not answering" : "Answering"} · added{" "}
          {new Date(install.created_at).toLocaleDateString()}
          {install.own_app && " · your own app"}
        </p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {confirmDisconnect ? (
          <>
            <Button variant="outline" size="sm" className="min-h-[44px]" onClick={() => setConfirmDisconnect(false)}>
              Cancel
            </Button>
            <Button
              size="sm"
              className="min-h-[44px] bg-red-600 hover:bg-red-700 text-white"
              onClick={handleDisconnect}
              disabled={disconnectMutation.isPending}
            >
              {disconnectMutation.isPending && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
              Remove from {platformLabel}
            </Button>
          </>
        ) : (
          <>
            <Button
              variant="outline"
              size="sm"
              className="min-h-[44px]"
              onClick={handleTogglePaused}
              disabled={setPausedMutation.isPending}
            >
              {isPaused ? <Play className="w-4 h-4 mr-1.5" /> : <Pause className="w-4 h-4 mr-1.5" />}
              {isPaused ? "Resume" : "Pause"}
            </Button>
            <Button variant="outline" size="sm" className="min-h-[44px]" onClick={() => setConfirmDisconnect(true)}>
              Disconnect
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
